/**
 * Report Service for Majalisna Application
 * 
 * This service provides methods for generating and downloading reports.
 */

import { privateApiClient } from '../apiClient';
import API from '../../config/api';

const reportService = {
  /**
   * Download a nomination report
   * @param {number} nominationId - The ID of the nomination
   * @param {Object} options - Report options
   * @param {boolean} options.includeEvaluation - Whether to include evaluation
   * @param {boolean} options.includeAttachmentA - Whether to include attachment A
   * @param {boolean} options.includeAttachmentB - Whether to include attachment B
   * @param {('pdf'|'zip')} options.format - Report format ('pdf' or 'zip'). XLSX is bulk-only.
   * @returns {Promise} - The axios promise with response type 'blob'
   */
  downloadNominationReport: (nominationId, options = {}) => {
    const {
      includeEvaluation = false,
      includeAttachmentA = false,
      includeAttachmentB = false,
      format = 'pdf'
    } = options;

    return privateApiClient.get(
      API.reports.nomination(nominationId),
      {
        params: {
          include_evaluation: includeEvaluation,
          include_attachment_a: includeAttachmentA,
          include_attachment_b: includeAttachmentB,
          format
        },
        responseType: 'blob',
        // Disable timeout to allow heavy server-side generation
        timeout: 0
      }
    );
  },

  /**
   * Bulk export reports for a branch or center
   * @param {Object} filters - Export filters
   * @param {string} filters.branchId - Branch CODE (e.g., 'jeddah', 'taif') (optional)
   * @param {number|string} filters.centerId - Center ID (numeric) (optional)
   * @param {string} filters.status - Nomination status (optional)
   * @param {Object} options - Report options
   * @param {boolean} options.includeEvaluation - Whether to include evaluations
   * @param {boolean} options.includeAttachments - Whether to include attachments
   * @param {boolean} options.allFields - Whether to include all database fields (XLSX only)
   * @param {('pdf'|'zip'|'xlsx')} options.format - Report format ('pdf', 'zip', or 'xlsx')
   * @returns {Promise} - The axios promise with response type 'blob'
   */
  bulkExportReports: (filters = {}, options = {}) => {
    const {
      branchId,
      centerId,
      status
    } = filters;

    const {
      includeEvaluation = false,
      includeAttachments = false,
      allFields = false,
      format = 'zip'
    } = options;

    const params = {};
    if (branchId != null && String(branchId).trim()) params.branch_id = String(branchId).trim();
    if (centerId != null && String(centerId).trim()) params.center_id = String(centerId).trim();
    if (status) params.status = status;
    params.include_evaluation = includeEvaluation;
    params.include_attachments = includeAttachments;
    params.all_fields = allFields;
    params.format = format;

    console.debug('[reportService] bulkExportReports params', params);
    return privateApiClient.get(API.reports.bulkExportReports, {
      params,
      responseType: 'blob',
      // Disable timeout to allow heavy server-side generation
      timeout: 0
    });
  },

  /**
   * Fetch nominations statistics (JSON)
   * @param {Object} filters
   * @param {string|number} [filters.branchId]
   * @param {string|number} [filters.centerId]
   */
  getNominationsStatistics: (filters = {}) => {
    const params = {};
    if (filters.branchId != null && String(filters.branchId).trim()) params.branch_id = String(filters.branchId).trim();
    if (filters.centerId != null && String(filters.centerId).trim()) params.center_id = String(filters.centerId).trim();
    return privateApiClient.get(API.reports.statisticsNominations, { params });
  },

  /**
   * Fetch evaluations statistics (JSON)
   * @param {Object} filters
   * @param {string|number} [filters.branchId]
   * @param {string|number} [filters.centerId]
   */
  getEvaluationsStatistics: (filters = {}) => {
    const params = {};
    if (filters.branchId != null && String(filters.branchId).trim()) params.branch_id = String(filters.branchId).trim();
    if (filters.centerId != null && String(filters.centerId).trim()) params.center_id = String(filters.centerId).trim();
    return privateApiClient.get(API.reports.statisticsEvaluations, { params });
  },

  /**
   * Fetch per-center nominations statistics (aggregated in one response)
   * @param {string} [branchId] Branch CODE (e.g., 'jeddah'). If omitted, backend uses role logic.
   */
  getCentersStatistics: (branchId) => {
    const params = {};
    if (branchId != null && String(branchId).trim()) params.branch_id = String(branchId).trim();
    return privateApiClient.get(API.reports.statisticsCenters, { params });
  }
};

export default reportService;
